/**
 * SIL-476 — Fleet detail tab data layer. Same `{ data, basis }` hook pattern
 * as `useEnvironmentalOverview.ts`/`useEnvironmentalCircularity.ts`: one
 * shared underlying query (`useFleetDetailQuery`,
 * `GET /api/client/environmental/fleet-detail`) for the widgets unique to
 * this tab (class breakdown, monthly MWh, site breakdown, top emitters).
 *
 * The KPI strip, monthly S2+S3 bars, FY30 target card, and second-life
 * ledger are DELIBERATELY not modeled here — they reuse
 * `useEnvironmentalOverview.ts`'s existing hooks
 * (`useEnvironmentalOverviewQuery`/`useAvoidedEventsTape`/
 * `useOrgCarbonTarget`) so this tab never re-fetches or disagrees with the
 * Overview tab about the org's totals or its avoided-events data.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { EnvironmentalFleetDetailResponse } from '@siliconx/shared';
import { api, throwIfNotOk } from '../lib/api';
import { qk } from '../lib/queryKeys';

export function useFleetDetailQuery(): UseQueryResult<EnvironmentalFleetDetailResponse> {
  return useQuery({
    queryKey: qk.environmental.fleetDetail(),
    queryFn: async () => {
      const res = await api.api.client.environmental['fleet-detail'].$get();
      await throwIfNotOk(res);
      return res.json();
    },
  });
}

export function useClassBreakdown(): {
  data: EnvironmentalFleetDetailResponse['classBreakdown']['items'] | undefined;
  basis: EnvironmentalFleetDetailResponse['classBreakdown']['basis'] | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
} {
  const q = useFleetDetailQuery();
  return {
    data: q.data?.classBreakdown.items,
    basis: q.data?.classBreakdown.basis,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
  };
}

export function useMonthlyMwh(): {
  data: EnvironmentalFleetDetailResponse['monthlyMwh'] | undefined;
  isLoading: boolean;
} {
  const q = useFleetDetailQuery();
  return { data: q.data?.monthlyMwh, isLoading: q.isLoading };
}

export function useSiteBreakdown(): {
  data: EnvironmentalFleetDetailResponse['siteBreakdown'] | undefined;
  isLoading: boolean;
} {
  const q = useFleetDetailQuery();
  return { data: q.data?.siteBreakdown, isLoading: q.isLoading };
}

export function useTopEmitters(): {
  data: EnvironmentalFleetDetailResponse['topEmitters']['items'] | undefined;
  basis: EnvironmentalFleetDetailResponse['topEmitters']['basis'] | undefined;
  isLoading: boolean;
} {
  const q = useFleetDetailQuery();
  return {
    data: q.data?.topEmitters.items,
    basis: q.data?.topEmitters.basis,
    isLoading: q.isLoading,
  };
}
